import React from 'react';
import { ShareIcon } from 'components/icons';
import { t } from '@lingui/macro';
import { OptionsMenu } from 'components/OptionsMenu';
import { OptionsMenuButton } from 'components/OptionsMenu/OptionsMenuButton';
import { Text, useDisclosure, VStack } from '@cardboard-ui/react';
import { DownloadButton } from './components/DownloadButton';
import { ShortcutButton } from './components/ShortcutButton';
import { CopyLinkButton } from './components/CopyLinkButton';
import { graphql, useFragment } from 'utils/graphClient';
import { ShareButton_node$key } from './__generated__/ShareButton_node.graphql';
import { AnnouncementButton } from './components/AnnouncementButton';
import { ReminderButton } from './components/ReminderButton';
import { WebShareButton } from './components/WebShareButton';
import { ItemMenuProps } from '../../ItemMenu';

const nodeFragment = graphql`
  fragment ShareButton_node on Node {
    __typename
    id
    ...CopyLinkButton_node
    ...WebShareButton_node
    ...DownloadButton_node
    ...AnnouncementButton_node
    ...ShortcutButton_node
    ...ReminderButton_node
  }
`;

interface ShareButtonProps {
  node: ShareButton_node$key;
  mode: ItemMenuProps['mode'];
}

export const ShareButton = ({ node: _node, mode }: ShareButtonProps) => {
  const node = useFragment(nodeFragment, _node);
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!node) return null;

  const showAdminShareOptions =
    node.__typename === 'Folder' ||
    node.__typename === 'FolderItem' ||
    node.__typename === 'Event' ||
    node.__typename === 'VaultItem';

  return (
    <OptionsMenu
      button={
        <OptionsMenuButton
          aria-label={t`Share options`}
          icon={<ShareIcon />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          onClick={onOpen}
        />
      }
      // isLazy // Can not use isLazy
      // Using isLazy here  breaks the 'Creating an announcement from a published item test'
      isOpen={isOpen}
      onClose={onClose}
    >
      <VStack px={2} py={1} spacing={2}>
        <CopyLinkButton node={node} closeMenu={onClose} />
        <DownloadButton
          node={node}
          closeMenu={onClose}
          w="100%"
          size="sm"
          inItemViewer={mode === 'item-viewer-bar'}
        />
        <WebShareButton node={node} />
      </VStack>
      {showAdminShareOptions && (
        <VStack w="100%" px={2}>
          <Text
            sx={{
              '&:only-child': {
                display: 'none',
              },
            }}
            pt={2}
            alignSelf="flex-start"
            fontSize="xs"
          >{t`Admin options`}</Text>

          <AnnouncementButton node={node} closeMenu={onClose} />
          <ShortcutButton node={node} closeMenu={onClose} />
          <ReminderButton node={node} closeMenu={onClose} />
        </VStack>
      )}
    </OptionsMenu>
  );
};
